import { useClient } from "#/hook/useClient";
import type { ItemLinkWarning } from "#/types/manifest";
import { formatDate } from "#/utils/dateTimeUtils";
import { formatHawbLabel } from "#/utils/manifestUtils";
import { Button, Modal, Text } from "@mantine/core";
import { IconAlertTriangle } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";

interface HawbLinkWarningsModalProps {
  opened: boolean;
  warnings: ItemLinkWarning[];
  loading: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * Shown before an item is linked to, moved between or unlinked from a
 * consignment whenever the backend preview finds something off. Nothing is
 * changed until the user confirms.
 */
const HawbLinkWarningsModal = ({
  opened,
  warnings,
  loading,
  onConfirm,
  onCancel,
}: HawbLinkWarningsModalProps) => {
  const { t } = useTranslation();
  const { client } = useClient();

  const describe = (warning: ItemLinkWarning): string => {
    const params = {
      hawb: formatHawbLabel(warning.hawb),
      from: warning.from_count ?? 0,
      to: warning.to_count ?? 0,
      packages: warning.packages ?? 0,
      date: warning.validated_at ? formatDate(warning.validated_at, client) : "--",
    };
    // Bulk assign can move several items out of one consignment at once.
    if (warning.code === "moving" && warning.item_ids.length > 1) {
      return t("items.hawbLink.warnings.movingMany", { ...params, count: warning.item_ids.length });
    }
    return t(`items.hawbLink.warnings.${warning.code}`, params);
  };

  return (
    <Modal
      opened={opened}
      onClose={() => {
        if (!loading) onCancel();
      }}
      title={t("items.hawbLink.confirmTitle")}
      centered
    >
      <div className="flex flex-col gap-4">
        <Text size="sm">{t("items.hawbLink.confirmIntro")}</Text>
        <ul className="flex flex-col gap-2">
          {warnings.map((warning, index) => (
            <li
              key={`${warning.code}-${warning.hawb.id}-${index}`}
              className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900"
            >
              <IconAlertTriangle size={16} className="mt-0.5 shrink-0 text-amber-600" />
              <span>{describe(warning)}</span>
            </li>
          ))}
        </ul>
        <div className="flex justify-end gap-2">
          <Button variant="outline" color="black" onClick={onCancel} disabled={loading}>
            {t("common.cancel")}
          </Button>
          <Button color="black" onClick={onConfirm} loading={loading}>
            {t("items.hawbLink.confirmButton")}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default HawbLinkWarningsModal;
